import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useScrollAnimation, fadeInUpVariants, staggerContainerVariants, scaleInVariants } from '../hooks/useScrollAnimation';

const BenefitsContainer = styled(motion.div)`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;
`;

const Title = styled(motion.h2)`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 16px;
  text-align: center;
  z-index: 1;
`;

const Subtitle = styled(motion.p)`
  font-size: 20px;
  color: var(--light-text);
  margin-bottom: 40px;
  text-align: center;
  max-width: 600px;
  z-index: 1;
`;

const RecipeCardsContainer = styled(motion.div)`
  display: flex;
  gap: 24px;
  margin-top: 20px;
  z-index: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const RecipeCard = styled(motion.div)`
  width: 280px;
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 20px;
  box-shadow: var(--shadow);
  border: 1px solid var(--glass-border);
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 320px;
  }
`;

const RecipeImage = styled.img`
  width: 100%;
  height: 160px;
  border-radius: 12px;
  object-fit: cover;
  margin-bottom: 16px;
`;

const RecipeTitle = styled.h3`
  font-size: 18px;
  margin-bottom: 8px;
`;

const RecipeInfo = styled.p`
  font-size: 14px;
  color: var(--light-text);
  margin-bottom: 16px;
`;

const AssistantQuote = styled(motion.div)`
  background: var(--primary-color);
  color: white;
  padding: 16px 24px;
  border-radius: 20px;
  border-bottom-left-radius: 4px;
  margin-top: 20px;
  max-width: 250px;
  position: relative;
  align-self: flex-start;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 10px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid var(--primary-color);
  }
`;

const BackgroundBlob = styled(motion.div)`
  position: absolute;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fd79a830, #6c5ce730);
  filter: blur(60px);
  opacity: 0.5;
  z-index: 0;
  right: -100px;
  top: 50%;
  transform: translateY(-50%);
`;

const Benefits = () => {
  const [containerRef, controls] = useScrollAnimation();

  const recipes = [
    {
      title: "Quick Breakfast Omelette",
      image: "/images/recipe1.jpg",
      time: "10 mins",
      ingredients: "Eggs, Cheese, Tomato"
    },
    {
      title: "Savory Pasta Dinner",
      image: "/images/recipe2.jpg",
      time: "20 mins",
      ingredients: "Pasta, Garlic, Tomato"
    },
    {
      title: "Hearty Vegetable Soup",
      image: "/images/recipe3.jpg",
      time: "25 mins",
      ingredients: "Carrot, Onion, Celery"
    }
  ];

  return (
    <BenefitsContainer
      ref={containerRef}
      variants={staggerContainerVariants}
      initial="hidden"
      animate={controls}
    >
      <BackgroundBlob 
        animate={{ 
          scale: [1, 1.1, 1],
          rotate: [0, -5, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse"
        }}
      />
      
      <Title variants={fadeInUpVariants}>
        Delicious Recipes
      </Title>
      
      <Subtitle variants={fadeInUpVariants}>
        Tired? Hungry? Let AI cook for you.
      </Subtitle>
      
      <RecipeCardsContainer
        variants={staggerContainerVariants}
      >
        {recipes.map((recipe, index) => (
          <RecipeCard
            key={index}
            variants={scaleInVariants}
            whileHover={{ y: -10, transition: { duration: 0.3 } }}
          >
            <RecipeImage src={recipe.image} alt={recipe.title} />
            <RecipeTitle>{recipe.title}</RecipeTitle>
            <RecipeInfo>⏰ {recipe.time} • 🧪 {recipe.ingredients}</RecipeInfo>
          </RecipeCard>
        ))}
      </RecipeCardsContainer>
      
      <AssistantQuote
        variants={fadeInUpVariants}
        animate={{ 
          y: [0, -5, 0] 
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatType: "reverse"
        }}
      >
        I've got you covered! 😺
      </AssistantQuote>
    </BenefitsContainer>
  );
};

export default Benefits; 